import React, { useState, useEffect } from 'react'
import { useForm, FormProvider, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { CreateUserData, createUserSchema } from '@shared'
import { UserService } from '@/services/userService'
import { getErrorMessage, isValidationError } from '@/utils/errorHandling'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import FormField from '@/components/ui/FormField'
import { useToast } from '@/hooks/useToast'

interface CreateUserModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  onError: (error: string) => void
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onError
}) => {
  const [createLoading, setCreateLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { toast } = useToast()

  // React Hook Form setup for creating users
  const methods = useForm<CreateUserData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: 'HR_ADMIN',
      branchId: ''
    }
  })

  const { handleSubmit, reset, setError: setFormError, control, setValue } = methods

  // Watch role to show/hide branchId field
  const selectedRole = useWatch({ control, name: 'role' })
  const showBranchId = selectedRole === 'BRANCH_MANAGER'

  // Clear branchId when switching away from BRANCH_MANAGER
  useEffect(() => {
    if (selectedRole !== 'BRANCH_MANAGER') {
      setValue('branchId', undefined)
    }
  }, [selectedRole, setValue])

  // Handle form submission
  const onSubmit = async (data: CreateUserData) => {
    try {
      setCreateLoading(true)
      onError('') // Clear any existing errors

      await UserService.createUser(data)

      // Close modal and reset form
      handleClose()
      onSuccess()
      toast.success('User created successfully', `${data.firstName} ${data.lastName} has been created successfully`)
    } catch (err) {
      // Handle form errors with type-safe approach
      if (isValidationError(err, 'email already exists')) {
        setFormError('email', {
          type: 'manual',
          message: 'A user with this email already exists'
        })
      } else {
        const errorMessage = getErrorMessage(err, 'Failed to create user')
        onError(errorMessage)
        toast.error('Creation failed', errorMessage)
      }
    } finally {
      setCreateLoading(false)
    }
  }

  // Helper to close modal and reset form state
  const handleClose = () => {
    reset()
    setShowPassword(false)
    onError('') // Clear errors
    onClose()
  }

  if (!isOpen) return null

  const roleOptions = [
    { value: 'HR_ADMIN', label: 'HR Admin' },
    { value: 'GENERAL_MANAGER', label: 'General Manager' },
    { value: 'BRANCH_MANAGER', label: 'Branch Manager' },
    { value: 'SYSTEM_ADMIN', label: 'System Admin' }
  ]

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg border border-border p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Create New User</h3>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
            disabled={createLoading}
          >
            ×
          </button>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              name="firstName"
              label="First Name"
              placeholder="Enter first name"
              required
              disabled={createLoading}
            />

            <FormField
              name="lastName"
              label="Last Name"
              placeholder="Enter last name"
              required
              disabled={createLoading}
            />

            <FormField
              name="email"
              label="Email Address"
              type="email"
              placeholder="Enter email address"
              required
              disabled={createLoading}
            />

            <FormField
              name="password"
              label="Password"
              type="password"
              inputType={showPassword ? 'text' : 'password'}
              placeholder="Enter password (min 8 characters)"
              required
              disabled={createLoading}
              rightIcon={showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              onRightIconClick={() => setShowPassword(!showPassword)}
            />

            <FormField
              name="role"
              label="Role"
              type="select"
              options={roleOptions}
              disabled={createLoading}
            />

            {showBranchId && (
              <FormField
                name="branchId"
                label="Store ID"
                placeholder="e.g., 001, 002, 003"
                required
                disabled={createLoading}
                helperText="The store ID this manager is responsible for"
              />
            )}

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={createLoading}
                className="px-4 py-2 text-sm border border-input rounded-md hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createLoading}
                className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {createLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                {createLoading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}

export default CreateUserModal 